import { Route, Routes, Outlet } from 'react-router-dom';

import { NotFound } from './components/NotFound';
import { ApplicationCycleProvider } from './Contexts/ApplicationCycleContext';
import { ConfigProvider } from './Contexts/ConfigContext';
import { ApplicationProvider } from './Contexts/DashboardContext';
import { LocaleProvider } from './Contexts/LocaleContext';
import { SelectedPartnerProvider } from './Contexts/SelectedPartnerContext';
import { SelectedProgramProvider } from './Contexts/SelectedProgramContext';
import { UsersContext, UsersProvider } from './Contexts/UsersContext';
import Application from './routes/Application';
import DashboardTemplate from './routes/DashboardTemplate';
import GrowthAward from './routes/GrowthAward';
import MyAccount from './routes/MyAccount';
import NotValid from './routes/NotValid';
import StateSpecific from './routes/StateSpecific';
import UserManagement from './routes/UserManagement';
import './App.css';
import Admin from './routes/Admin';
import GenericError from './routes/GenericError';
import Inactive from './routes/Inactive';
import ApplicationPreview from './routes/ApplicationPreview';
import SubmitConfirmation from './routes/SubmitConfirmation';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone'; // dependent on utc plugin
import NewPartnerIntake from './routes/NewPartnerIntake';
import NewUserIntake from './routes/NewUserIntake';
import { Spinner } from './components/common/Spinner';
import { MsalAuthenticationResult, MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { useContext } from 'react';
import { TokenContext } from './Contexts/TokenContext';
import { trackAuthRedirect } from './utils/authTelemetry';

function App() {
  const { isTokenMissing } = useContext(TokenContext);
  const { userCurrent } = useContext(UsersContext);

  dayjs.extend(utc);
  dayjs.extend(timezone);

  // Wrapper component for routes that need SelectedProgramProvider
  const ProtectedRoutesWithProgram = () => {
    if (isTokenMissing || userCurrent === null) {
      return <Spinner />;
    }

    const partnerScopeKey = String(userCurrent.currentPartnerId ?? 'none');

    return (
      <SelectedPartnerProvider key={partnerScopeKey} partnerId={userCurrent.currentPartnerId}>
        <SelectedProgramProvider>
          <ApplicationProvider>
            <ApplicationCycleProvider>
              <Outlet />
            </ApplicationCycleProvider>
          </ApplicationProvider>
        </SelectedProgramProvider>
      </SelectedPartnerProvider>
    );
  };

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      loadingComponent={() => <Spinner />}
      errorComponent={(err: MsalAuthenticationResult) => {
        if (err?.error?.errorCode === 'access_denied') {
          trackAuthRedirect('MsalTemplateError', { error: err.error });
          void err.login();
        }

        return <Spinner />;
      }}
    >
      <UsersProvider>
        <LocaleProvider>
          <ConfigProvider>
            <Routes>
              <Route
                path='/addaccount'
                element={
                  <SelectedPartnerProvider
                    key={String(userCurrent.currentPartnerId ?? 'none')}
                    partnerId={userCurrent.currentPartnerId}
                  >
                    <NewPartnerIntake />
                  </SelectedPartnerProvider>
                }
              />

              {/* AddAccount - no SelectedProgramProvider */}

              {/* Protected Routes with SelectedProgramProvider */}
              <Route element={<ProtectedRoutesWithProgram />}>
                <Route path='/signup' element={<NewUserIntake />} />
                <Route path='/pending' element={<NotValid />} />
                <Route path='/inactive' element={<Inactive />} />
                <Route path='/error' element={<GenericError />} />
                <Route path='/' element={<DashboardTemplate />} />
                <Route path='/dashboard' element={<DashboardTemplate />} />
                <Route path='/application/:appid' element={<Application />} />
                <Route path='/submit-confirmation' element={<SubmitConfirmation />} />
                <Route
                  path='/application-preview/:applicationCycleId'
                  element={<ApplicationPreview />}
                />
                <Route path='/growthaward/:appid' element={<GrowthAward />} />
                <Route path='/statequestions/:appid' element={<StateSpecific />} />
                <Route path='/usermanagement' element={<UserManagement />} />
                <Route path='/myaccount' element={<MyAccount />} />
                <Route path='/admin' element={<Admin />} />
              </Route>

              {/* Catch all */}
              <Route path='*' element={<NotFound />} />
            </Routes>
          </ConfigProvider>
        </LocaleProvider>
      </UsersProvider>
    </MsalAuthenticationTemplate>
  );
}

export default App;
